import React from 'react'
import Link from '../components/Link'
import {
  CheckIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ArrowPathIcon,
  LifebuoyIcon,
} from '@heroicons/react/24/outline'

const Pricing: React.FC = () => {
  const pricingTiers = [
    {
      name: 'Basic',
      price: '$250 per school',
      features: [
        'Unlimited Classes',
        'Unlimited Students',
        'Unlimited Usage',
        '15 Users per school',
        '48 hour support response time',
      ],
    },
    {
      name: 'Advanced',
      price: '$350 per school',
      features: [
        'Unlimited Classes',
        'Unlimited Students',
        'Unlimited Usage',
        '20 Users per school',
        '24 hour support response time',
        'Full-service onboarding',
      ],
    },
    {
      name: 'Enterprise',
      price: 'Contact Sales',
      features: [
        'Unlimited Classes',
        'Unlimited Students',
        'Unlimited Usage',
        'Unlimited Users',
        '24 hour support response time',
        'Full-service onboarding',
      ],
      featured: true,
    },
  ]

  const platformFeatures = [
    {
      icon: ClipboardDocumentListIcon,
      title: 'Accommodations Tracking',
      description:
        'Document and monitor daily accommodations for each student with ease',
    },
    {
      icon: ChartBarIcon,
      title: 'Goal Progress Reports',
      description:
        'Track student goal data and generate compelling progress reports',
    },
    {
      icon: DocumentTextIcon,
      title: 'Report Generation',
      description:
        'Create data-driven reports to share with stakeholders and guide IEP conversations',
    },
    {
      icon: BuildingOfficeIcon,
      title: 'Data Administration',
      description:
        'Manage Schools, Classes, Users, and Students in one centralized location',
    },
    {
      icon: ArrowPathIcon,
      title: 'Data Import',
      description:
        'Seamlessly import your existing student and class data to get started quickly',
    },
    {
      icon: LifebuoyIcon,
      title: 'Customer Support',
      description:
        'Dedicated support team ready to help you succeed with TrackIEP',
    },
  ]

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <main className="flex-1 overflow-auto min-h-0">
        {/* Header Section */}
        <section className="py-12 px-4 md:p-12 text-center">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Choose the plan that fits your school's needs. All plans include
            unlimited accommodations and goal tracking.
          </p>
        </section>

        {/* Pricing Cards */}
        <section className="py-12 px-4 md:px-12 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingTiers.map((tier) => (
              <div
                key={tier.name}
                className="bg-white rounded-lg shadow-lg p-8 flex flex-col"
              >
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
                  {tier.name}
                </h2>
                <p className="text-3xl font-extrabold text-blue-600 text-center mb-2">
                  {tier.price}
                </p>
                <h2 className="text-xl font-bold text-gray-500 text-center mb-6">
                  Annual Billing
                </h2>
                <ul className="space-y-4 mb-8 flex-1">
                  {tier.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  variant={tier.featured ? 'primary' : 'secondary'}
                  to="/request-demo"
                  className={`w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md ${
                    tier.featured
                      ? 'text-white bg-blue-600 hover:bg-blue-700'
                      : 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`}
                >
                  {tier.name === 'Enterprise' ? 'Contact Sales' : 'Get Started'}
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* Features Section */}
        <section className="bg-white py-16 px-4 md:px-12">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
              Everything You Need to Streamline IEP Management
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
              All plans include access to our complete platform with powerful
              features designed for educators.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {platformFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center text-center p-6 bg-gray-50 rounded-lg shadow-lg"
                >
                  <feature.icon className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-blue-600 py-16 px-4 md:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Streamline Your IEP Management?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Join educators who are saving time and improving compliance with
              TrackIEP.
            </p>
            <Link
              variant="primary"
              to="/request-demo"
              className="md:max-w-xs relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-white hover:bg-gray-100 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 mx-auto"
            >
              Request a Demo
            </Link>
          </div>
        </section>
      </main>
    </div>
  )
}

export default Pricing
